import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// One-time global fetch wrapper: every same-origin or backend-bound request
// automatically carries credentials so the session cookie is sent.
// This avoids touching every fetch() call site across the frontend.
if (typeof window !== 'undefined' && !window.__authFetchPatched) {
  const originalFetch = window.fetch.bind(window);
  // Endpoints that intentionally invalidate / probe the session cookie. A 401
  // from these is expected and must NOT trigger an auto-redirect to /login
  // (otherwise the disconnect/logout response gets clobbered mid-flight,
  // surfacing as a "body stream already read" error in the calling code).
  const AUTH_BYPASS = ['/api/auth/me', '/api/auth/logout', '/api/gmail/disconnect'];
  window.fetch = (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    const isBackend = BACKEND && url.startsWith(BACKEND);
    const isApiPath = url.startsWith('/api/');
    if (isBackend || isApiPath) {
      init = { credentials: 'include', ...init };
    }
    const promise = originalFetch(input, init);
    // Auto-redirect to /login on 401 (skip auth-related endpoints whose 401
    // is expected and self-handled by the caller).
    return promise.then((res) => {
      const isBypass = AUTH_BYPASS.some(p => url.includes(p));
      if (res.status === 401 && isBackend && !isBypass) {
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }
      return res;
    });
  };
  window.__authFetchPatched = true;
}

const AuthContext = createContext({ user: null, loading: true, logout: () => {}, refresh: () => {} });

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND}/api/auth/me`, { credentials: 'include' });
      if (r.ok) setUser(await r.json());
      else setUser(null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${BACKEND}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch { /* swallow */ }
    setUser(null);
    window.location.href = '/login';
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
